import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { getMockImportLanding } from '../../../services';

const BATCH_PARAM = 'batch';

/**
 * The import batch the list opens with, read once on mount: from the navigation state a bulk
 * import lands with (`state.importBatch = { id, ... }`, which also marks its rows New) or, on a
 * direct load, from the `?batch=<id>` URL param (no New badge).
 *
 * @returns {{ batchId: string, autoAppliedBatchId: string }}
 */
export const useInitialImportBatch = () => {
  const { state } = useLocation();
  const [params] = useSearchParams();

  const [initial] = useState(() => {
    // `getMockImportLanding` is the `?mockImport=1` QA switch: null (and inert) otherwise.
    const fromState = state?.importBatch?.id ?? getMockImportLanding()?.id ?? '';
    return { batchId: fromState || params.get(BATCH_PARAM) || '', autoAppliedBatchId: fromState };
  });

  return initial;
};

/**
 * Keeps the URL in step with the applied batch (`?batch=<id>`, the id only: no personal data) and
 * clears the one-shot navigation state from history.
 *
 * @param {string} importBatch The batch id the filters hold ('' for none).
 */
export const useImportBatchUrlSync = (importBatch) => {
  const { state } = useLocation();
  const [params, setParams] = useSearchParams();
  const current = params.get(BATCH_PARAM) ?? '';
  const hasNavigationState = Boolean(state?.importBatch);

  useEffect(() => {
    if (current === importBatch && !hasNavigationState) return;

    const next = new URLSearchParams(params);
    if (importBatch) next.set(BATCH_PARAM, importBatch);
    else next.delete(BATCH_PARAM);
    setParams(next, { replace: true, state: null });
  }, [importBatch, current, hasNavigationState, params, setParams]);
};
